import { FC, useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import Tooltip from '../tooltip/Tooltip';
import Loader from '../loader/Loader';
import { InfoSVG, PlusCircleSvg } from '../svgs';
import SpecimenLotInfo from '../specimen-lot-info/SpecimenLotInfo';
import AddStrawLevelDataModal from '../AddStrawLevelData/AddStrawLevelDataModal';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { getOnFarmAnimalById, getOnFarmSpecimensByOwnership } from '../../services/onFarmInventory.service';
import { getAnimalById } from '../../api/animalsApi';
import { INVENTORY_CONSTANTS, LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { toastMessages } from '../../constants/errorMessages';
import { showToast } from '../../services/toast.service';
import { specimenNeedsStrawDetails } from '../../utils/commonUtils';
import { AddStrawLevelDataModalMode, AddStrawModalState } from '../../types/interfaces/addStrawLevelData.interfaces';
import { Specimen, SpecimenInventory } from '../../types/interfaces/specimen.interfaces';
import { Genders, InventorySource, TransactionStatusEnum } from '../../types/enums';

export type SelectedSpecimenQuantity = {
  availableQuantity: number;
  embryoGrade?: string;
  embryoStage?: string;
  quantity: number;
  selected: boolean;
  selectedCanisterId?: number;
  selectable?: boolean;
  specimenId: number;
  specimenLocationId?: number;
  embryoStrawId?: number;
  embryoStrawCode?: string;
  embryoStrawNumber?: string;
  embryoCountPerStraw?: number;
  flushMating?: string;
  isEmbryo?: boolean;
  selectedEmbryoStrawIds?: Number[];
};

type SpecimenQuantitiesProps = {
  animalId?: number;
  accountId?: number;
  animalGender?: string;
  onChange: (e: { valid: boolean; value: SelectedSpecimenQuantity[] }) => void;
  allowToAddStrawDetails?: boolean;
  onMissingEmbryoStraws?: (specimenIds: number[]) => void;
  inventorySource?: InventorySource;
};

const SelectSpecimens: FC<SpecimenQuantitiesProps> = ({
  animalId,
  accountId,
  onChange,
  animalGender,
  allowToAddStrawDetails = false,
  onMissingEmbryoStraws,
  inventorySource = InventorySource.RSG,
}): JSX.Element => {
  const user = useAppSelector(state => state.user);

  const [loading, setLoading] = useState<boolean>(false);
  const [quantities, setQuantities] = useState<SelectedSpecimenQuantity[]>([]);
  const [specimens, setSpecimens] = useState<Specimen[]>();
  const [addStrawModalState, setAddStrawModalState] = useState<AddStrawModalState | null>(null);
  const [genderFromApi, setGenderFromApi] = useState<string>();

  useEffect(() => {
    const fetchAnimal = async () => {
      if (!animalId) {
        setGenderFromApi(undefined);
        return;
      }
      try {
        const response =
          inventorySource === InventorySource.ON_FARM
            ? await getOnFarmAnimalById(animalId)
            : await getAnimalById(String(animalId));
        setGenderFromApi(response.data.gender);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    };
    fetchAnimal();
  }, [animalId, inventorySource]);

  const isFemale = (genderFromApi ?? animalGender)?.toLowerCase() === Genders.Female.toLowerCase();

  const handleGetSpecimens = useCallback(async () => {
    if (!animalId || !accountId) {
      setSpecimens([]);
      setQuantities([]);
      return;
    }

    try {
      setLoading(true);

      const params = {
        include: 'Animal, SpecimenType, SpecimenLocation, EmbryoStraw, SpecimenCustomDataValue',
        userId: user.userId,
        getUpdatedAvailableQuantities: true,
        excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
      };

      const specs =
        inventorySource === InventorySource.ON_FARM
          ? await getOnFarmSpecimensByOwnership(animalId, accountId, params)
          : await getSpecimensByOwnership(animalId, accountId, params);

      const specimenRows: SpecimenInventory[] = [];
      const quants: SelectedSpecimenQuantity[] = [];

      let embryoMissingSpecimenIds: number[] = [];

      for (const spec of specs.data) {
        const flushMating =
          spec.specimenCustomDataValues?.find(item => item.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ?? '-';

        if (isFemale && spec.quantityAvailable > 0 && spec.embryoStraws?.length === 0) {
          embryoMissingSpecimenIds.push(spec.specimenId);
        }

        if (isFemale) {
          if (Array.isArray(spec.embryoStraws) && spec.embryoStraws.length > 0) {
            spec.embryoStraws.forEach(straw => {
              const availableQuantity = straw.embryoCountPerStraw ?? spec.quantityAvailable ?? 0;
              if (availableQuantity <= 0) return;

              specimenRows.push({
                ...spec,
                embryoStrawCode: straw.embryoStrawCode,
                embryoStrawId: straw.embryoStrawId,
                embryoStrawNumber: straw.embryoStrawNumber,
                embryoGrade: straw.embryoGrade,
                embryoStage: straw.embryoStage,
              });

              quants.push({
                specimenId: spec.specimenId,
                availableQuantity,
                selectedCanisterId: spec.specimenLocations?.[0]?.storageCanisterId,
                quantity: 0,
                selected: false,
                embryoStrawCode: straw.embryoStrawCode,
                embryoStrawId: straw.embryoStrawId,
                embryoStrawNumber: straw.embryoStrawNumber,
                embryoGrade: straw.embryoGrade,
                embryoStage: straw.embryoStage,
                flushMating,
              });
            });
          } else if ((spec.quantityAvailable ?? 0) > 0) {
            const needsStrawDetails = specimenNeedsStrawDetails(spec);

            specimenRows.push(spec);
            quants.push({
              specimenId: spec.specimenId,
              availableQuantity: spec.quantityAvailable ?? 0,
              selectedCanisterId: spec.specimenLocations?.[0]?.storageCanisterId,
              quantity: 0,
              selected: false,
              selectable: needsStrawDetails ? false : undefined,
              flushMating,
            });
          }
        } else if ((spec.quantityAvailable ?? 0) > 0) {
          specimenRows.push(spec);
          quants.push({
            specimenId: spec.specimenId,
            availableQuantity: spec.quantityAvailable ?? 0,
            selectedCanisterId: spec.specimenLocations?.[0]?.storageCanisterId,
            quantity: 0,
            selected: false,
          });
        }
      }
      onMissingEmbryoStraws?.(embryoMissingSpecimenIds);
      setSpecimens(specimenRows);
      setQuantities(quants);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setLoading(false);
    }
  }, [animalId, accountId, isFemale, user.userId, inventorySource]);

  const handleAddStrawOpen = (specimenId: number) => {
    setAddStrawModalState({ specimenId });
  };

  const renderStrawLotContent = (spec: Specimen) => {
    if (spec.embryoStrawCode) {
      return (
        <>
          {spec.embryoStrawCode}
          <Tooltip>
            {`${LABEL_CONSTANTS.EMBRYO_GRADE}: ${spec.embryoGrade || 'NA'}`} <br />
            {`${LABEL_CONSTANTS.EMBRYO_STAGE}: ${spec.embryoStage || 'NA'}`}
          </Tooltip>
        </>
      );
    }

    if (allowToAddStrawDetails && specimenNeedsStrawDetails(spec)) {
      return (
        <button className="button icon add-straw-details" type="button" onClick={() => handleAddStrawOpen(spec.specimenId)}>
          <PlusCircleSvg />
        </button>
      );
    }

    return '-';
  };

  const inputCheckbox = (index: number) => {
    return (
      <input
        type="checkbox"
        checked={quantities[index]?.selected ?? false}
        //TODO: Remove isFemale check, when the embryo audit is done
        disabled={isFemale || quantities[index]?.selectable === false}
        onChange={e => {
          const checked = e.target.checked;
          const newQuantities = [...quantities];
          newQuantities[index] = {
            ...quantities[index],
            selected: checked,
            quantity: checked ? quantities[index]?.availableQuantity ?? 0 : 0,
          };
          setQuantities(newQuantities);
        }}
      />
    );
  };

  const inputQuantityElement = (index: number) => {
    return (
      <input
        type="text"
        placeholder="0"
        value={quantities[index]?.selected ? (quantities[index]?.quantity > 0 ? quantities[index]?.quantity : '') : '-'}
        disabled={!quantities[index]?.selected}
        onChange={e => {
          const count = +e.target.value
            .replace(/[^\d]+/, '') 
            .replace(/^0+/, ''); 
          let newQuantities = [...quantities];
          newQuantities[index] = { ...quantities[index], quantity: count };
          setQuantities(newQuantities);
        }}
      />
    );
  };

  useEffect(() => {
    handleGetSpecimens();
  }, [handleGetSpecimens]);

  useEffect(() => {
    const quants = quantities.filter(q => q.selected);
    const valid = quants.reduce((acc: boolean, q: SelectedSpecimenQuantity): boolean => {
      return acc && q.quantity > 0 && q.quantity <= q.availableQuantity;
    }, quants.length > 0);
    onChange({ valid: valid, value: quants });
  }, [quantities]);

  const handleErrors = (index: number) => {
    return quantities[index]?.selected
      ? quantities[index]?.quantity > quantities[index]?.availableQuantity
        ? 'error'
        : quantities[index]?.quantity === 0
        ? 'warning'
        : ''
      : '';
  };

  return loading ? (
    <Loader loaderSize="small" addedSpace />
  ) : (
    <>
      {/* TODO: Remove this block when the embryo audit is done */}
      {isFemale && (
        <div className="info-banner">
          <InfoSVG size="18" color="#0f64dcf5" />
          {INVENTORY_CONSTANTS.EMBRYO_AUDIT_IN_PROGRESS_MESSAGE}
        </div>
      )}
      <div className="desk-select-specimens">
        <div className="specimen-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.SELECT}</th>
                <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                <th>{TABLE_HEADER_CONSTANTS.LOT_NO}</th>
                {isFemale ? (
                  <>
                    <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                    <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                    <th>{TABLE_HEADER_CONSTANTS.FLUSH_MATING}</th>
                  </>
                ) : (
                  <th>{TABLE_HEADER_CONSTANTS.QUALITY}</th>
                )}
                <th>{isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                <th>{TABLE_HEADER_CONSTANTS.QUANTITY}</th>
              </tr>
            </thead>

            <tbody>
              {specimens && specimens.length > 0 ? (
                specimens.map((spec, index) => {
                  return (
                    <tr className={handleErrors(index)} key={'specimen-row:' + spec.specimenId + '-index:' + index}>
                      <td>{inputCheckbox(index)}</td>
                      <td>
                        {spec.animal?.code}-{spec.animal?.name}
                      </td>
                      <td>{spec.specimenType?.name}</td>
                      <td>{<SpecimenLotInfo specimenId={spec.specimenId} lotNumber={spec?.lotNumber} />}</td>
                      {isFemale ? (
                        <>
                          <td>{renderStrawLotContent(spec)}</td>
                          <td>{quantities[index]?.embryoStrawNumber ?? '-'}</td>
                          <td>{quantities[index]?.flushMating ?? '-'}</td>
                        </>
                      ) : (
                        <td>{spec.quality}</td>
                      )}

                      <td>{quantities[index]?.availableQuantity}</td>
                      <td>
                        {isFemale ? (
                          <input
                            type="text"
                            value={quantities[index]?.selected ? quantities[index]?.availableQuantity ?? 0 : '-'}
                            disabled
                            className="quantity-input"
                          />
                        ) : (
                          inputQuantityElement(index)
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={isFemale ? 7 : 6} className="no-records-found">
                    {LABEL_CONSTANTS.NO_RESULTS_FOUND}
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={isFemale ? 8 : 6}>{LABEL_CONSTANTS.TOTAL_QUANTITY}:</td>
                <td>
                  <input
                    type="text"
                    disabled
                    value={quantities.reduce(
                      (acc: number, qua: SelectedSpecimenQuantity): number => (qua.selected ? acc + qua.quantity : acc),
                      0,
                    )}
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/** Mobile View */}
      <div className="xs-select-specimens">
        {specimens && specimens.length > 0 ? (
          specimens.map((spec, index) => {
            return (
              <div
                className={`card max-width ${handleErrors(index)}`}
                key={'specimen-row:' + spec.specimenId + '-index:' + index}>
                <div className="selected-specimens-row">
                  <div className="input-check">{inputCheckbox(index)}</div>
                  <div className="select-specimens">
                    <div className="card-content-section">
                      <div className="left column">
                        <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                        <label>{`${spec.animal?.code} - ${spec.animal?.name}`}</label>
                      </div>
                      <div className="left column">
                        <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                        <label>{`${spec.specimenType?.name}`}</label>
                      </div>
                      <div className="right column">
                        <label>{`${TABLE_HEADER_CONSTANTS.LOT_NO}: `}</label>
                        <label>{<SpecimenLotInfo specimenId={spec.specimenId} lotNumber={spec?.lotNumber} />}</label>
                      </div>
                    </div>
                    <div className="card-content-section">
                      {!isFemale && (
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.QUALITY}: `}</label>
                          <label>{`${spec.quality}`}</label>
                        </div>
                      )}
                      <div className="left column ">
                        <label>
                          {`${isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}
                        </label>
                        <label>{`${quantities[index]?.availableQuantity}`}</label>
                      </div>

                      <div className="right center column">
                        <label>{`${TABLE_HEADER_CONSTANTS.QUANTITY}:`}</label>
                        {inputQuantityElement(index)}
                      </div>
                    </div>
                    {isFemale && (
                      <div className="card-content-section">
                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                          <label>{renderStrawLotContent(spec)}</label>
                        </div>

                        <div className="left column">
                          <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                          <label>{quantities[index]?.embryoStrawNumber ?? '-'}</label>
                        </div>

                        <div className="right column">
                          <label>{`${TABLE_HEADER_CONSTANTS.FLUSH_MATING}: `}</label>
                          <label>{quantities[index]?.flushMating ?? '-'}</label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-records-found">{LABEL_CONSTANTS.NO_RESULTS_FOUND}</div>
        )}
        <div className="total-quantity">
          <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp; </label>
          {quantities.reduce(
            (acc: number, qua: SelectedSpecimenQuantity): number => (qua.selected ? acc + qua.quantity : acc),
            0,
          )}
        </div>
      </div>

      {addStrawModalState && (
        <AddStrawLevelDataModal
          isOpen
          onClose={() => setAddStrawModalState(null)}
          onSuccess={handleGetSpecimens}
          mode={AddStrawLevelDataModalMode.Specimen}
          specimenIds={[addStrawModalState.specimenId]}
          preselectedSpecimenId={addStrawModalState.specimenId}
        />
      )}
    </>
  );
};

export default SelectSpecimens;
